import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Textarea } from 'components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { 
  ArrowLeft, Clock, Play, Square, CheckCircle, AlertCircle, 
  Download, FileText, Calendar, Timer, Flag, MapPin, Navigation,
  BarChart3, AlertTriangle, Edit, TrendingUp, TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';

export default function CareHours() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [activeShift, setActiveShift] = useState(null);
  const [summary, setSummary] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [clockOutOpen, setClockOutOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [clockInOpen, setClockInOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [selectedShiftForFlag, setSelectedShiftForFlag] = useState(null);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [clientStartTime, setClientStartTime] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [editRequests, setEditRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('shifts');

  const isCaregiver = user?.role === 'independent_caregiver';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let interval;
    if (activeShift) {
      const updateElapsed = () => {
        // Use client-side start time for fresh clock-ins to avoid server/client clock drift
        const diff = clientStartTime
          ? Date.now() - clientStartTime
          : new Date() - new Date(activeShift.clock_in);
        const absDiff = Math.max(0, diff);
        const hours = Math.floor(absDiff / 3600000);
        const minutes = Math.floor((absDiff % 3600000) / 60000);
        const seconds = Math.floor((absDiff % 60000) / 1000);
        setElapsedTime(
          `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        );
      };
      updateElapsed();
      interval = setInterval(updateElapsed, 1000);
    } else {
      setElapsedTime('00:00:00');
    }
    return () => clearInterval(interval);
  }, [activeShift, clientStartTime]);

  const fetchData = async () => {
    try {
      const [shiftsRes, summaryRes, activeRes] = await Promise.all([
        api.get(`/shifts`),
        api.get(`/shifts/summary`),
        isCaregiver ? api.get(`/shifts/active`) : Promise.resolve({ data: { shift: null } })
      ]);
      setShifts(shiftsRes.data.shifts || []);
      setSummary(summaryRes.data);
      setActiveShift(activeRes.data.shift);

      if (isCaregiver) {
        // For caregivers, fetch their assigned clients from care circles
        try {
          const circlesRes = await api.get(`/care-circles/my-circles`);
          const circles = circlesRes.data.circles || [];
          
          // Extract unique profiles from circles the caregiver is a member of
          const assignedProfiles = [];
          const seenProfileIds = new Set();
          
          for (const circle of circles) {
            if (circle.profile_id && !seenProfileIds.has(circle.profile_id)) {
              seenProfileIds.add(circle.profile_id);
              assignedProfiles.push({
                profile_id: circle.profile_id,
                care_recipient_name: circle.care_recipient_name || circle.name || 'Care Recipient',
                user_id: circle.owner_id
              });
            }
          }
          setProfiles(assignedProfiles);
        } catch (error) {
          console.error('Failed to fetch assigned clients:', error);
          setProfiles([]);
        }
      } else {
        const [profilesRes, comparisonRes, editRequestsRes] = await Promise.all([
          api.get(`/care-profiles`),
          api.get(`/shifts/comparison`),
          api.get(`/shifts/edit-requests`)
        ]);
        setProfiles(profilesRes.data.profiles || []);
        setComparison(comparisonRes.data);
        setEditRequests(editRequestsRes.data.edit_requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get current GPS location
  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  };

  const handleClockIn = async () => {
    if (!selectedProfile) {
      toast.error('Please select a care profile');
      return;
    }
    
    setGettingLocation(true);
    let location = null;
    
    try {
      // Try to get GPS location
      toast.info('Getting your location...', { duration: 2000 });
      const gpsLocation = await getCurrentLocation();
      // Format location for backend (lat/lng format)
      location = {
        lat: gpsLocation.latitude,
        lng: gpsLocation.longitude,
        accuracy: gpsLocation.accuracy
      };
      setCurrentLocation(location);
      toast.success('Location captured!', { duration: 1500 });
    } catch (error) {
      console.warn('Could not get location:', error.message);
      toast.warning('Location unavailable - proceeding without GPS', { duration: 3000 });
    }
    
    try {
      const profile = profiles.find(p => p.profile_id === selectedProfile);
      const response = await api.post(`/shifts/clock-in`, {
        client_id: profile?.user_id || user.user_id,
        profile_id: selectedProfile,
        location: location
      });
      
      if (response.data.shift) {
        setClientStartTime(Date.now());
        setActiveShift(response.data.shift);
      }

      if (response.data.location_verified === true) {
        toast.success(`Clocked in! Location verified ✓ (${response.data.location_distance}m from expected)`);
      } else if (response.data.location_verified === false) {
        toast.warning(`Clocked in, but location needs review (${response.data.location_distance}m away)`);
      } else {
        toast.success('Clocked in successfully!');
      }

      setClockInOpen(false);
      setGettingLocation(false);
      fetchData();
    } catch (error) {
      setGettingLocation(false);
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    if (!clockOutNotes.trim()) {
      toast.error('Please add shift notes');
      return;
    }
    
    setGettingLocation(true);
    let location = null;
    
    try {
      toast.info('Getting your location...', { duration: 2000 });
      const gpsLocation = await getCurrentLocation();
      // Format location for backend (lat/lng format)
      location = {
        lat: gpsLocation.latitude,
        lng: gpsLocation.longitude,
        accuracy: gpsLocation.accuracy
      };
      toast.success('Location captured!', { duration: 1500 });
    } catch (error) {
      console.warn('Could not get location:', error.message);
      toast.warning('Location unavailable - proceeding without GPS', { duration: 3000 });
    }
    
    try {
      // Backend expects POST /shifts/clock-out (not /shifts/{id}/clock-out)
      const response = await api.post(`/shifts/clock-out`, {
        notes: clockOutNotes,
        location: location
      });
      
      if (response.data.location_verified === true) {
        toast.success('Clocked out! Location verified ✓');
      } else {
        toast.success('Clocked out successfully!');
      }
      
      setClockOutNotes('');
      setClockOutOpen(false);
      setClientStartTime(null);
      setActiveShift(null);
      setGettingLocation(false);
      fetchData();
    } catch (error) {
      setGettingLocation(false);
      toast.error(error.response?.data?.detail || 'Failed to clock out');
    }
  };

  const handleApprove = async (shiftId) => {
    try {
      await api.post(`/shifts/${shiftId}/approve`, {});
      toast.success('Shift approved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve shift');
    }
  };

  const handleFlag = async () => {
    if (!selectedShiftForFlag) return;
    try {
      await api.post(`/shifts/${selectedShiftForFlag}/flag`, {
        reason: flagReason
      });
      toast.success('Shift flagged for review');
      setFlagOpen(false);
      setFlagReason('');
      setSelectedShiftForFlag(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to flag shift');
    }
  };

  const handleApproveEdit = async (editId) => {
    try {
      await api.post(`/shifts/edit-requests/${editId}/approve`, {});
      toast.success('Edit request approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve edit request');
    }
  };

  const handleRejectEdit = async (editId) => {
    try {
      await api.post(`/shifts/edit-requests/${editId}/reject`, { reason: 'Declined by family' });
      toast.success('Edit request rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject edit request');
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/shifts/export?format=${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timesheet.${format === 'csv' ? 'csv' : 'html'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'pending_approval':
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-teal-100 text-teal-700">Approved</Badge>;
      case 'flagged':
        return <Badge className="bg-rose-100 text-rose-700">Flagged</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse-soft">
          <div className="w-12 h-12 rounded-full bg-teal-500/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-teal-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="care-hours">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Care Hours™</h1>
              <p className="text-xs sm:text-sm text-slate-500">Track and manage caregiver shifts</p>
            </div>
          </div>
          <div className="hidden sm:flex gap-2">
            <Button variant="outline" onClick={() => handleExport('csv')} data-testid="export-csv-btn">
              <Download className="w-4 h-4" />
              CSV
            </Button>
            <Button variant="outline" onClick={() => handleExport('pdf')} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4" />
              PDF
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Active Shift / Clock In */}
        {isCaregiver && (
          <Card className={activeShift ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white border-0' : ''}>
            <CardContent className="p-6">
              {activeShift ? (
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                      <Timer className="w-8 h-8" />
                    </div>
                    <div>
                      <p className="text-green-100">Active Shift</p>
                      <p className="text-2xl sm:text-4xl font-bold font-['Manrope']">{elapsedTime}</p>
                      <p className="text-green-100 text-sm">
                        Started {new Date(activeShift.clock_in).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <Dialog open={clockOutOpen} onOpenChange={setClockOutOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-white text-green-700 hover:bg-green-50" size="lg" data-testid="clock-out-btn">
                        <Square className="w-5 h-5" />
                        Clock Out
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Clock Out</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Shift Notes (Required)</label>
                          <Textarea
                            placeholder="Describe the activities and any observations from this shift..."
                            value={clockOutNotes}
                            onChange={(e) => setClockOutNotes(e.target.value)}
                            className="min-h-[120px]"
                            data-testid="shift-notes-textarea"
                          />
                        </div>
                        
                        {/* GPS Location Info */}
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-teal-500" />
                            <span className="font-medium text-slate-700">Location will be recorded</span>
                          </div>
                        </div>
                        
                        <Button
                          onClick={handleClockOut}
                          disabled={gettingLocation}
                          className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                          data-testid="confirm-clock-out-btn"
                        >
                          {gettingLocation ? (
                            <>
                              <Navigation className="w-4 h-4 animate-pulse" />
                              Getting Location...
                            </>
                          ) : (
                            'Confirm Clock Out'
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : (
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
                      <Clock className="w-8 h-8 text-slate-400" />
                    </div>
                    <div>
                      <p className="text-slate-500">No Active Shift</p>
                      <p className="text-xl font-semibold text-slate-900">Ready to start working?</p>
                    </div>
                  </div>
                  <Dialog open={clockInOpen} onOpenChange={setClockInOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-teal-500 hover:bg-teal-600 text-white" size="lg" data-testid="clock-in-btn">
                        <Play className="w-5 h-5" />
                        Clock In
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Start New Shift</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Select Client</label>
                          {profiles.length === 0 ? (
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-amber-800 text-sm font-medium">No Clients Assigned</p>
                              <p className="text-amber-700 text-xs mt-1">
                                You need to be added to a care circle by a family before you can clock in. 
                                Contact your client's family to get added to their care team.
                              </p>
                            </div>
                          ) : (
                            <Select value={selectedProfile} onValueChange={setSelectedProfile}>
                              <SelectTrigger data-testid="profile-select">
                                <SelectValue placeholder="Choose a care profile" />
                              </SelectTrigger>
                              <SelectContent>
                                {profiles.map((profile) => (
                                  <SelectItem key={profile.profile_id} value={profile.profile_id}>
                                    {profile.care_recipient_name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        
                        {/* GPS Location Info */}
                        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-teal-500" />
                            <span className="font-medium text-slate-700">GPS Location Tracking</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">
                            Your location will be recorded when you clock in to verify you're at the care location.
                          </p>
                        </div>
                        
                        <Button
                          onClick={handleClockIn}
                          disabled={gettingLocation || profiles.length === 0}
                          className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                          data-testid="confirm-clock-in-btn"
                        >
                          {gettingLocation ? (
                            <>
                              <Navigation className="w-4 h-4 animate-pulse" />
                              Getting Location...
                            </>
                          ) : (
                            <>
                              <MapPin className="w-4 h-4" />
                              Start Shift with Location
                            </>
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{summary.week_hours || 0}h</p>
              <p className="text-sm text-slate-500">This Week</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{summary.total_hours || 0}h</p>
              <p className="text-sm text-slate-500">Total Hours</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{summary.pending_hours || 0}h</p>
              <p className="text-sm text-slate-500">Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{summary.total_shifts || 0}</p>
              <p className="text-sm text-slate-500">Total Shifts</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Family Caregivers */}
        {!isCaregiver && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList className="bg-slate-100">
              <TabsTrigger value="shifts" data-testid="tab-shifts">Shift History</TabsTrigger>
              <TabsTrigger value="comparison" data-testid="tab-comparison">
                <BarChart3 className="w-4 h-4 mr-2" />
                Hours Comparison
              </TabsTrigger>
              {editRequests.length > 0 && (
                <TabsTrigger value="edits" data-testid="tab-edits">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Requests ({editRequests.length})
                </TabsTrigger>
              )}
            </TabsList>

            {/* Hours Comparison Tab */}
            <TabsContent value="comparison">
              {comparison?.comparison ? (
                <div className="space-y-6">
                  {/* Discrepancies Alert */}
                  {comparison.discrepancies?.length > 0 && (
                    <div className="space-y-2">
                      {comparison.discrepancies.map((d, i) => (
                        <div key={i} className={`p-4 rounded-lg flex items-start gap-3 ${
                          d.severity === 'high' ? 'bg-rose-50 border border-rose-200' : 
                          d.severity === 'medium' ? 'bg-amber-50 border border-amber-200' : 
                          'bg-blue-50 border border-blue-200'
                        }`}>
                          <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                            d.severity === 'high' ? 'text-rose-500' : 
                            d.severity === 'medium' ? 'text-amber-500' : 'text-blue-500'
                          }`} />
                          <div>
                            <p className="font-medium">{d.message}</p>
                            <p className="text-sm text-slate-600 mt-1">{d.action}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Comparison Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-slate-500">Authorized Hours</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl sm:text-3xl font-bold">{comparison.comparison.authorized_hours || 0}h</div>
                        <p className="text-sm text-slate-500">This period</p>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-slate-500">Actual Hours</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl sm:text-3xl font-bold text-teal-600">{comparison.comparison.approved_hours || 0}h</span>
                          <span className="text-slate-500">approved</span>
                        </div>
                        <div className="text-sm text-amber-600">+{comparison.comparison.pending_hours || 0}h pending</div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-slate-500">Variance</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(() => {
                          const approved = Number(comparison.comparison.approved_hours) || 0;
                          const authorized = Number(comparison.comparison.authorized_hours) || 0;
                          const variance = approved - authorized;
                          if (approved === 0 && authorized === 0) {
                            return (
                              <div data-testid="variance-no-data">
                                <div className="text-2xl sm:text-3xl font-bold text-slate-400">0h</div>
                                <p className="text-sm text-slate-400">No data yet</p>
                              </div>
                            );
                          }
                          const isOver = variance > 0;
                          return (
                            <>
                              <div className={`text-2xl sm:text-3xl font-bold flex items-center gap-2 ${isOver ? 'text-rose-600' : 'text-teal-600'}`}>
                                {isOver ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
                                {Math.abs(variance).toFixed(1)}h
                              </div>
                              <p className={`text-sm ${isOver ? 'text-rose-600' : 'text-teal-600'}`}>
                                {isOver ? 'Over authorized' : 'Under authorized'}
                              </p>
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Weekly Breakdown */}
                  {comparison.weekly_breakdown && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="font-['Manrope']">Weekly Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {comparison.weekly_breakdown.map((week) => (
                            <div key={week.week} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium">Week {week.week}</p>
                                <p className="text-sm text-slate-500">{week.start_date} - {week.end_date}</p>
                              </div>
                              <div className="flex items-center gap-3 sm:gap-6 text-sm flex-wrap">
                                <div className="text-right">
                                  <p className="font-semibold">{week.approved_hours}h</p>
                                  <p className="text-slate-500">Approved</p>
                                </div>
                                <div className="text-right">
                                  <p className="font-semibold text-amber-600">{week.pending_hours}h</p>
                                  <p className="text-slate-500">Pending</p>
                                </div>
                                <div className="text-right">
                                  <p className={`font-semibold ${week.variance > 0 ? 'text-rose-600' : week.variance < 0 ? 'text-teal-600' : ''}`}>
                                    {week.variance > 0 ? '+' : ''}{week.variance}h
                                  </p>
                                  <p className="text-slate-500">Variance</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              ) : (
                <div className="text-center py-12" data-testid="comparison-no-budget">
                  <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Comparison Data</h3>
                  <p className="text-slate-500 max-w-md mx-auto">
                    {comparison?.message || 'Create a budget with authorized hours to see how actual hours compare.'}
                  </p>
                </div>
              )}
            </TabsContent>
            <TabsContent value="edits">
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Manrope']">Pending Edit Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  {editRequests.length === 0 ? (
                    <div className="text-center py-8">
                      <Edit className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No pending edit requests</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {editRequests.map((req) => (
                        <div key={req.edit_id} className="p-4 border rounded-lg" data-testid={`edit-request-${req.edit_id}`}>
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="font-medium">{req.caregiver_name}</p>
                              <p className="text-sm text-slate-500">Shift on {req.shift_date}</p>
                            </div>
                            <Badge className="bg-amber-100 text-amber-700">Pending Review</Badge>
                          </div>
                          <div className="bg-slate-50 p-3 rounded-lg mb-3">
                            <p className="text-sm">
                              <span className="text-slate-500">Requested change:</span>{' '}
                              <span className="font-medium">{req.original_hours}h → {req.requested_hours}h</span>
                            </p>
                            {req.reason && (
                              <p className="text-sm mt-1">
                                <span className="text-slate-500">Reason:</span> {req.reason}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              onClick={() => handleApproveEdit(req.edit_id)}
                              className="bg-teal-500 hover:bg-teal-600"
                              data-testid={`approve-edit-${req.edit_id}`}
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleRejectEdit(req.edit_id)}
                              data-testid={`reject-edit-${req.edit_id}`}
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Shifts Tab - Keep existing */}
            <TabsContent value="shifts">
              <Card>
                <CardHeader>
                  <CardTitle className="font-['Manrope']">Shift History</CardTitle>
                </CardHeader>
                <CardContent>
                  {shifts.length === 0 ? (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No shifts recorded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {shifts.map((shift) => (
                        <div
                          key={shift.shift_id}
                          className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-slate-50 rounded-xl gap-4"
                          data-testid={`shift-${shift.shift_id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                              <Calendar className="w-6 h-6 text-slate-400" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                {getStatusBadge(shift.status)}
                                {shift.caregiver_name && (
                                  <span className="text-sm text-slate-500">by {shift.caregiver_name}</span>
                                )}
                              </div>
                              <p className="text-sm text-slate-600">
                                {new Date(shift.clock_in).toLocaleDateString()} • {' '}
                                {new Date(shift.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                {shift.clock_out && (
                                  <> – {new Date(shift.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                                )}
                              </p>
                              {shift.notes && (
                                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{shift.notes}</p>
                              )}
                              {shift.flagged_reason && (
                                <p className="text-sm text-rose-600 mt-1">Flagged: {shift.flagged_reason}</p>
                              )}
                              {shift.location_verified !== undefined && (
                                <div className="flex items-center gap-1 mt-1">
                                  <MapPin className={`w-3 h-3 ${shift.location_verified ? 'text-teal-500' : 'text-amber-500'}`} />
                                  <span className={`text-xs ${shift.location_verified ? 'text-teal-600' : 'text-amber-600'}`}>
                                    {shift.location_verified ? 'Location verified' : `Location unverified (${shift.location_distance}m away)`}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-bold">{shift.total_hours?.toFixed(1) || '—'}h</p>
                            </div>
                            {shift.status === 'pending_approval' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleApprove(shift.shift_id)}
                                  className="bg-teal-500 hover:bg-teal-600"
                                  data-testid={`approve-shift-${shift.shift_id}`}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Approve
                                </Button>
                                <Dialog open={flagOpen && selectedShiftForFlag === shift.shift_id} onOpenChange={(open) => {
                                  setFlagOpen(open);
                                  if (!open) setSelectedShiftForFlag(null);
                                }}>
                                  <DialogTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setSelectedShiftForFlag(shift.shift_id)}
                                      data-testid={`flag-shift-${shift.shift_id}`}
                                    >
                                      <Flag className="w-4 h-4" />
                                      Flag
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Flag Shift for Review</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-4 pt-4">
                                      <div className="space-y-2">
                                        <label className="text-sm font-medium">Reason for flagging</label>
                                        <Textarea
                                          placeholder="Explain why this shift needs review..."
                                          value={flagReason}
                                          onChange={(e) => setFlagReason(e.target.value)}
                                        />
                                      </div>
                                      <Button onClick={handleFlag} className="w-full bg-rose-500 hover:bg-rose-600">
                                        Submit Flag
                                      </Button>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        {/* Shifts List for Caregivers (no tabs) */}
        {isCaregiver && (
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope']">Shift History</CardTitle>
            </CardHeader>
            <CardContent>
              {shifts.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No shifts recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {shifts.map((shift) => (
                    <div
                      key={shift.shift_id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-slate-50 rounded-xl gap-4"
                      data-testid={`shift-${shift.shift_id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-slate-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            {getStatusBadge(shift.status)}
                          </div>
                          <p className="text-sm text-slate-600">
                            {new Date(shift.clock_in).toLocaleDateString()} • {' '}
                            {new Date(shift.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            {shift.clock_out && (
                              <> – {new Date(shift.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                            )}
                          </p>
                          {shift.notes && (
                            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{shift.notes}</p>
                          )}
                          {shift.location_verified !== undefined && (
                            <div className="flex items-center gap-1 mt-1">
                              <MapPin className={`w-3 h-3 ${shift.location_verified ? 'text-teal-500' : 'text-amber-500'}`} />
                              <span className={`text-xs ${shift.location_verified ? 'text-teal-600' : 'text-amber-600'}`}>
                                {shift.location_verified ? 'Location verified' : `Location unverified (${shift.location_distance}m away)`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">{shift.total_hours?.toFixed(1) || '—'}h</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Flag Dialog */}
        <Dialog open={flagOpen} onOpenChange={setFlagOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Flag Shift for Review</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Reason for flagging</label>
                <Textarea
                  placeholder="Describe the issue with this shift..."
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              <Button
                onClick={handleFlag}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white"
              >
                Submit Flag
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
